import { Badge, Button, Card, Code, Drawer, Group, Modal, ScrollArea, Select, SimpleGrid, Stack, Table, Tabs, Text, TextInput, Textarea } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ErrorBox, notifyError, notifyOk } from "../../components/Errors";
import { CancelButton, JobStatusBadge, useKindLabel } from "../../components/Jobs";
import { PageTitle } from "../../components/Layout";
import { download, formatDate, formatLimit, get, patch, post, del, type AccessRequest, type AdminUser, type Job, type LimitSpec, type Plan, type Usage } from "../../lib/api";
import { useMe, useRegistry, useSpecs } from "../../lib/hooks";
import { LimitInput, planValueLabel } from "./LimitInput";
import { ExamplesAccessTab } from "./ExamplesTab";
import { ParamSetsTab } from "./ParamSetsTab";
import { FeedbackTab } from "./FeedbackTab";
import { ServicesTab } from "./ServicesTab";

type UserRow = AdminUser & { usage: Usage };

export function Admin() {
  const { data: me } = useMe();
  const [tab, setTab] = useState<string | null>("users");
  if (me && me.role !== "admin") return <Text>Administrators only.</Text>;
  return (
    <>
      <PageTitle title="Admin" />
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="users">Users</Tabs.Tab>
          <Tabs.Tab value="requests">Access requests <OpenRequestsBadge /></Tabs.Tab>
          <Tabs.Tab value="plans">Plans</Tabs.Tab>
          <Tabs.Tab value="jobs">Running jobs</Tabs.Tab>
          <Tabs.Tab value="paramsets">Parameter sets</Tabs.Tab>
          <Tabs.Tab value="examples">Illustrative Projects</Tabs.Tab>
          <Tabs.Tab value="services">Structure databases</Tabs.Tab>
          <Tabs.Tab value="feedback">Feedback</Tabs.Tab>
          <Tabs.Tab value="maintenance">Clean-up & reports</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="users"><UsersTab /></Tabs.Panel>
        <Tabs.Panel value="requests"><RequestsTab /></Tabs.Panel>
        <Tabs.Panel value="plans"><PlansTab /></Tabs.Panel>
        <Tabs.Panel value="jobs"><AdminJobsTab /></Tabs.Panel>
        <Tabs.Panel value="paramsets"><ParamSetsTab /></Tabs.Panel>
        <Tabs.Panel value="examples"><ExamplesAccessTab /></Tabs.Panel>
        <Tabs.Panel value="services"><ServicesTab /></Tabs.Panel>
        <Tabs.Panel value="feedback"><FeedbackTab /></Tabs.Panel>
        <Tabs.Panel value="maintenance"><MaintenanceTab /></Tabs.Panel>
      </Tabs>
    </>
  );
}

function OpenRequestsBadge() {
  const q = useQuery({ queryKey: ["admin-requests", "open"], queryFn: () => get<AccessRequest[]>("/admin/access-requests?status=open") });
  return q.data?.length ? <Badge size="xs" color="orange" ml={4}>{q.data.length}</Badge> : null;
}

// -- users ----------------------------------------------------------------------------------------------------
function UsersTab() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string | null>("");
  const [selected, setSelected] = useState<number | null>(null);
  const users = useQuery({
    queryKey: ["admin-users", q, status],
    queryFn: () => get<UserRow[]>(`/admin/users?q=${encodeURIComponent(q)}&status=${status ?? ""}`),
  });
  return (
    <Card withBorder>
      <Group mb="sm">
        <TextInput placeholder="Search e-mail, name, organization, country" value={q} onChange={(e) => setQ(e.currentTarget.value)} w={260} />
        <Select w={180} value={status} onChange={setStatus} allowDeselect={false}
          data={[{ value: "", label: "All accounts" }, { value: "pending", label: "Waiting for approval" }, { value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }]} />
      </Group>
      <ErrorBox error={users.error} />
      <Table.ScrollContainer minWidth={800}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr><Table.Th>User</Table.Th><Table.Th>Plan</Table.Th><Table.Th>Status</Table.Th><Table.Th>Role</Table.Th>
              <Table.Th>Storage</Table.Th><Table.Th>CPU this month</Table.Th><Table.Th>Last login</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.data?.map((u) => (
              <Table.Tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setSelected(u.id)}>
                <Table.Td><Text size="sm" fw={500}>{u.name || u.email}</Text><Text size="xs" c="dimmed">{u.email}</Text>
                  {(u.organization || u.country) && <Text size="xs" c="dimmed">{[u.organization, u.country].filter(Boolean).join(" · ")}</Text>}</Table.Td>
                <Table.Td>{u.plan_name}{Object.keys(u.overrides).length ? <Badge ml={4} size="xs" variant="outline">+{Object.keys(u.overrides).length}</Badge> : null}</Table.Td>
                <Table.Td><Badge variant="light" color={u.status === "active" ? "teal" : u.status === "pending" ? "orange" : "gray"}>{u.status}</Badge></Table.Td>
                <Table.Td>{u.role}</Table.Td>
                <Table.Td>{u.usage.storage_mb.toFixed(1)} MB · {u.usage.files} files</Table.Td>
                <Table.Td>{u.usage.cpu_hours_month.toFixed(2)} CPU-h</Table.Td>
                <Table.Td>{formatDate(u.last_login, true)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <UserDrawer userId={selected} onClose={() => setSelected(null)} />
    </Card>
  );
}

function UserDrawer({ userId, onClose }: { userId: number | null; onClose: () => void }) {
  const { data: reg } = useRegistry();
  const plans = useQuery({ queryKey: ["admin-plans"], queryFn: () => get<Plan[]>("/admin/plans") });
  const user = useQuery({ queryKey: ["admin-user", userId], queryFn: () => get<UserRow>(`/admin/users/${userId}`), enabled: userId != null });
  const qc = useQueryClient();
  const [plan, setPlan] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (user.data) {
      setPlan(user.data.plan);
      setRole(user.data.role);
      setStatus(user.data.status);
      setOverrides({ ...user.data.overrides });
    }
  }, [user.data]);

  const save = useMutation({
    mutationFn: () => patch(`/admin/users/${userId}`, { plan, role, status, overrides }),
    onSuccess: () => {
      notifyOk("Saved.");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const planLimits = plans.data?.find((p) => p.key === plan)?.limits ?? {};
  const specs = reg?.limits ?? [];
  const products = Array.from(new Set(specs.map((s) => s.product)));

  return (
    <Drawer opened={userId != null} onClose={onClose} position="right" size="xl" title={user.data ? (user.data.name || user.data.email) : "User"}>
      {user.data && (
        <Stack>
          <Text size="sm" c="dimmed">{user.data.email} · joined {formatDate(user.data.created_at)}</Text>
          <Text size="sm" data-testid="user-affiliation">
            <Text span c="dimmed">Organization: </Text>{user.data.organization || "-"}
            <Text span c="dimmed" ml="md">Country: </Text>{user.data.country || "-"}
          </Text>
          <SimpleGrid cols={3}>
            <Select label="Plan" value={plan} onChange={setPlan} allowDeselect={false}
              data={(plans.data ?? []).map((p) => ({ value: p.key, label: p.name }))} />
            <Select label="Status" value={status} onChange={setStatus} allowDeselect={false}
              data={[{ value: "active", label: "Active" }, { value: "pending", label: "Waiting for approval" }, { value: "disabled", label: "Disabled" }]} />
            <Select label="Role" value={role} onChange={setRole} allowDeselect={false}
              data={[{ value: "user", label: "User" }, { value: "group_lead", label: "Group lead" }, { value: "admin", label: "Administrator" }]} />
          </SimpleGrid>
          <Text fw={600} mt="sm">Personal limits</Text>
          <Text size="xs" c="dimmed">Leave a limit on "Plan" to use the plan's value; set a value to override it for this user only.</Text>
          <ScrollArea.Autosize mah="55vh">
            <Stack gap="md">
              {products.map((prod) => (
                <Table key={prod} withTableBorder>
                  <Table.Tbody>
                    {specs.filter((s) => s.product === prod).map((s) => (
                      <Table.Tr key={s.key}>
                        <Table.Td w="50%"><Text size="sm">{s.label}</Text></Table.Td>
                        <Table.Td>
                          <LimitInput spec={s} value={overrides[s.key]} inheritLabel={planValueLabel(s, planLimits[s.key] ?? s.fallback)}
                            onChange={(v) => setOverrides((o) => {
                              const n = { ...o };
                              if (v === undefined) delete n[s.key]; else n[s.key] = v;
                              return n;
                            })} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ))}
            </Stack>
          </ScrollArea.Autosize>
          <ErrorBox error={save.error} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Close</Button>
            <Button onClick={() => save.mutate()} loading={save.isPending}>Save</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

// -- access requests ------------------------------------------------------------------------------------------
function RequestsTab() {
  const [status, setStatus] = useState<string | null>("open");
  const q = useQuery({ queryKey: ["admin-requests", status], queryFn: () => get<AccessRequest[]>(`/admin/access-requests?status=${status ?? ""}`) });
  const specs = useSpecs();
  const [deciding, setDeciding] = useState<AccessRequest | null>(null);
  return (
    <Card withBorder>
      <Select mb="sm" w={200} value={status} onChange={setStatus} allowDeselect={false}
        data={[{ value: "open", label: "Open" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }, { value: "", label: "All" }]} />
      {!q.data?.length ? <Text c="dimmed" size="sm">No requests.</Text> : (
        <Table.ScrollContainer minWidth={700}>
          <Table>
            <Table.Thead><Table.Tr><Table.Th>User</Table.Th><Table.Th>Asks for</Table.Th><Table.Th>Why</Table.Th><Table.Th>When</Table.Th><Table.Th /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {q.data.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{r.user_email}</Table.Td>
                  <Table.Td>{specs[r.limit_key]?.label ?? r.limit_key}{r.requested_value ? `: ${r.requested_value}` : ""}</Table.Td>
                  <Table.Td><Text size="sm" lineClamp={3}>{r.message}</Text></Table.Td>
                  <Table.Td>{formatDate(r.created_at)}</Table.Td>
                  <Table.Td>{r.status === "open" ? <Button size="xs" onClick={() => setDeciding(r)}>Decide</Button> : <Badge variant="light">{r.status}</Badge>}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {deciding && <DecideModal req={deciding} spec={specs[deciding.limit_key]} onClose={() => setDeciding(null)} />}
    </Card>
  );
}

function DecideModal({ req, spec, onClose }: { req: AccessRequest; spec?: LimitSpec; onClose: () => void }) {
  const [value, setValue] = useState<unknown>(spec?.kind === "bool" ? true : req.requested_value != null ? Number(req.requested_value) : undefined);
  const [note, setNote] = useState("");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (approve: boolean) => post(`/admin/access-requests/${req.id}`, { approve, value: approve ? value : null, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-requests"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    },
  });
  return (
    <Modal opened onClose={onClose} title="Access request" centered>
      <Stack>
        <Text size="sm"><b>{req.user_email}</b> asks for <b>{spec?.label ?? req.limit_key}</b>{req.requested_value ? `: ${req.requested_value}` : ""}.</Text>
        {req.message && <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>{req.message}</Text>}
        {spec && (
          <Group>
            <Text size="sm">Grant:</Text>
            <LimitInput spec={spec} value={value} onChange={setValue} />
          </Group>
        )}
        <Textarea label="Note to the user (optional)" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
        <ErrorBox error={m.error} />
        <Group justify="flex-end">
          <Button color="red" variant="light" loading={m.isPending} onClick={() => m.mutate(false)}>Reject</Button>
          <Button loading={m.isPending} onClick={() => m.mutate(true)}>Approve</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// -- plans ----------------------------------------------------------------------------------------------------
function PlansTab() {
  const { data: reg } = useRegistry();
  const plans = useQuery({ queryKey: ["admin-plans"], queryFn: () => get<Plan[]>("/admin/plans") });
  const [editing, setEditing] = useState<Plan | null>(null);
  const [creating, setCreating] = useState(false);
  const specs = reg?.limits ?? [];
  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Text size="sm" c="dimmed">Changes to a plan apply at once to everyone on it (personal overrides stay).</Text>
        <Button size="xs" onClick={() => setCreating(true)}>New plan</Button>
      </Group>
      <Table.ScrollContainer minWidth={600}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Limit</Table.Th>
              {plans.data?.map((p) => (
                <Table.Th key={p.key}>
                  <Group gap={6}>
                    {p.name} <Text span size="xs" c="dimmed">({p.users} users)</Text>
                    <Button size="compact-xs" variant="subtle" onClick={() => setEditing(p)}>Edit</Button>
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {specs.map((s) => (
              <Table.Tr key={s.key}>
                <Table.Td><Text size="sm">{s.label}</Text></Table.Td>
                {plans.data?.map((p) => <Table.Td key={p.key}><Text size="sm">{formatLimit(s, p.limits[s.key] ?? s.fallback)}</Text></Table.Td>)}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {editing && <PlanEditor plan={editing} specs={specs} onClose={() => setEditing(null)} />}
      <NewPlanModal opened={creating} onClose={() => setCreating(false)} plans={plans.data ?? []} />
    </Card>
  );
}

function PlanEditor({ plan, specs, onClose }: { plan: Plan; specs: LimitSpec[]; onClose: () => void }) {
  const [name, setName] = useState(plan.name);
  const [values, setValues] = useState<Record<string, unknown>>({ ...plan.limits });
  const qc = useQueryClient();
  const changed = useMemo(() => Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== undefined && v !== plan.limits[k])), [values, plan]);
  const save = useMutation({
    mutationFn: () => patch(`/admin/plans/${plan.key}`, { name, limits: changed }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-plans"] }); qc.invalidateQueries({ queryKey: ["me"] }); notifyOk("Plan saved."); onClose(); },
  });
  const remove = useMutation({
    mutationFn: () => del(`/admin/plans/${plan.key}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-plans"] }); onClose(); },
    onError: (e) => notifyError(e),
  });
  return (
    <Drawer opened onClose={onClose} position="right" size="lg" title={`Plan: ${plan.name}`}>
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <ScrollArea.Autosize mah="60vh">
          <Table withTableBorder>
            <Table.Tbody>
              {specs.map((s) => (
                <Table.Tr key={s.key}>
                  <Table.Td w="50%"><Text size="sm">{s.label}</Text></Table.Td>
                  <Table.Td><LimitInput spec={s} value={values[s.key] ?? s.fallback} onChange={(v) => setValues((o) => ({ ...o, [s.key]: v }))} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
        <ErrorBox error={save.error} />
        <Group justify="space-between">
          {!["base", "advanced"].includes(plan.key)
            ? <Button color="red" variant="subtle" onClick={() => remove.mutate()} loading={remove.isPending}>Delete plan</Button>
            : <span />}
          <Group>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button onClick={() => save.mutate()} loading={save.isPending}>Save {Object.keys(changed).length ? `(${Object.keys(changed).length} changes)` : ""}</Button>
          </Group>
        </Group>
      </Stack>
    </Drawer>
  );
}

function NewPlanModal({ opened, onClose, plans }: { opened: boolean; onClose: () => void; plans: Plan[] }) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [copy, setCopy] = useState<string | null>("base");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post("/admin/plans", { key, name, copy_from: copy }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-plans"] }); onClose(); setKey(""); setName(""); },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="New plan" centered>
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="e.g. Partner institute" />
        <TextInput label="Key" description="lower case letters, digits, - and _" value={key} onChange={(e) => setKey(e.currentTarget.value.toLowerCase())} />
        <Select label="Start from" value={copy} onChange={setCopy} data={plans.map((p) => ({ value: p.key, label: p.name }))} allowDeselect={false} />
        <ErrorBox error={m.error} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!key || !name}>Create</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// -- jobs, clean-up, reports ----------------------------------------------------------------------------------
function AdminJobsTab() {
  const label = useKindLabel();
  const q = useQuery({ queryKey: ["admin-jobs"], queryFn: () => get<Job[]>("/admin/jobs?active=true"), refetchInterval: 5000 });
  return (
    <Card withBorder>
      {!q.data?.length ? <Text c="dimmed" size="sm">No jobs are running or queued.</Text> : (
        <Table>
          <Table.Thead><Table.Tr><Table.Th>Job</Table.Th><Table.Th>User id</Table.Th><Table.Th>Status</Table.Th><Table.Th>Queue</Table.Th><Table.Th>Progress</Table.Th><Table.Th>Submitted</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {q.data.map((j) => (
              <Table.Tr key={j.id}>
                <Table.Td>{label(j.kind)}</Table.Td>
                <Table.Td>{j.owner_id}</Table.Td>
                <Table.Td><JobStatusBadge status={j.status} /></Table.Td>
                <Table.Td>{j.queue}</Table.Td>
                <Table.Td>{Math.round(j.progress * 100)}%</Table.Td>
                <Table.Td>{formatDate(j.created_at, true)}</Table.Td>
                <Table.Td><CancelButton job={j} admin /></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}

type AuditRow = { id: number; at: string; actor_id: number | null; action: string; target: string; detail: Record<string, unknown> };

function MaintenanceTab() {
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  const clean = useMutation({ mutationFn: (dry: boolean) => post(`/admin/cleanup?dry_run=${dry}`), onSuccess: setReport, onError: (e) => notifyError(e) });
  const audit = useQuery({ queryKey: ["admin-audit"], queryFn: () => get<AuditRow[]>("/admin/audit?limit=200") });
  return (
    <Stack>
      <Card withBorder>
        <Text fw={600}>Clean-up</Text>
        <Text size="sm" c="dimmed" mb="sm">Runs every night at 02:00 IST. "Check" shows what would be deleted without changing anything.</Text>
        <Group>
          <Button variant="default" onClick={() => clean.mutate(true)} loading={clean.isPending}>Check</Button>
          <Button color="red" variant="light" onClick={() => clean.mutate(false)} loading={clean.isPending}>Run now</Button>
          <Button variant="default" onClick={() => download("/admin/usage.csv", "prayog_usage.csv").catch(notifyError)}>Usage report (CSV)</Button>
        </Group>
        {report && <Code block mt="sm">{JSON.stringify(report, null, 2)}</Code>}
      </Card>
      <Card withBorder>
        <Text fw={600} mb="xs">Audit log (latest 200)</Text>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing={4}>
            <Table.Thead><Table.Tr><Table.Th>When</Table.Th><Table.Th>Who</Table.Th><Table.Th>Action</Table.Th><Table.Th>Target</Table.Th><Table.Th>Details</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {audit.data?.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td><Text size="xs">{formatDate(a.at, true)}</Text></Table.Td>
                  <Table.Td><Text size="xs">{a.actor_id ?? "system"}</Text></Table.Td>
                  <Table.Td><Text size="xs">{a.action}</Text></Table.Td>
                  <Table.Td><Text size="xs">{a.target}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{JSON.stringify(a.detail)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}

